import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';

import { useCopyMemoMutation } from '../graphql/CopyMemo.generated';

interface UseCopyMemoInput {
  onSuccess?: (newMemoId: string) => void;
}

interface CopyMemoArgs {
  householdId: string;
  memoId: string;
}

export function useCopyMemo({ onSuccess }: UseCopyMemoInput = {}) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [copyMemoMutation, { loading }] = useCopyMemoMutation({
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      showFeedback('Memo duplicated successfully', { variant: 'success' });
      onSuccess?.(data.copyMemo.id);
    },
    onError: (error) => {
      showFeedback('Failed to duplicate memo');
      reportError('Failed to duplicate memo', error);
    },
  });

  const copyMemo = useCallback(
    ({ householdId, memoId }: CopyMemoArgs) => {
      return copyMemoMutation({
        variables: {
          input: {
            householdID: householdId,
            memoID: memoId,
          },
        },
      });
    },
    [copyMemoMutation]
  );

  return { copyMemo, loading };
}
